import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteStats, testimonials, faqData } from '@/mocks/comics';
import { OrganizationSchema, WebSiteSchema, WebPageSchema, BreadcrumbListSchema, FAQPageSchema } from '@/components/base/SchemaOrg';

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <>
      <OrganizationSchema />
      <WebSiteSchema />
      <WebPageSchema
        title="关于奇妙漫画 - 品牌介绍与联系方式"
        description="了解奇妙漫画的品牌故事、核心价值与用户口碑。奇妙漫画汇集海量漫画资源，致力于为中文读者提供优质的免费漫画阅读体验。"
        url="https://www.qimiaomanhua.com/about"
      />
      <BreadcrumbListSchema
        items={[
          { name: '首页', url: 'https://www.qimiaomanhua.com' },
          { name: '关于我们', url: 'https://www.qimiaomanhua.com/about' },
        ]}
      />
      <FAQPageSchema questions={faqData.map((f) => ({ question: f.question, answer: f.answer }))} />
      <Navbar />
      <main className="pt-16">
        {/* Page Header */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="flex items-center justify-center gap-2 text-xs text-foreground-400 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 font-medium">关于我们</span>
            </nav>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-5">
              关于奇妙漫画
            </h1>
            <p className="text-foreground-500 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              奇妙漫画是一个专注于为中文读者提供优质漫画阅读体验的在线平台，
              汇集海量韩漫、日漫、国漫资源，致力于打造最懂你的漫画图书馆。
            </p>
          </div>
        </section>

        {/* Brand Story */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <div>
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
                  品牌故事
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  为漫画爱好者而生
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    奇妙漫画诞生于一群漫画狂热爱好者的共同梦想。我们发现，尽管漫画文化在中国蓬勃发展，
                    但依然缺少一个真正从读者角度出发的、体验优秀的漫画阅读平台。
                  </p>
                  <p>
                    于是，奇妙漫画应运而生。我们投入大量精力优化阅读体验，首创下拉式连续阅读模式，
                    让读者可以像刷社交媒体一样流畅地阅读漫画，无需逐页翻动，沉浸在故事之中。
                  </p>
                  <p>
                    从最初的几十部作品，到如今汇集超过{siteStats.totalComics.toLocaleString()}部漫画、
                    {siteStats.totalChapters.toLocaleString()}个章节的海量资源库，
                    奇妙漫画始终坚持免费阅读的理念，让每一个热爱漫画的人都能无门槛地享受阅读的快乐。
                  </p>
                  <p>
                    我们相信，好的漫画值得被更多人看到，漫画文化值得被更好地传播。
                    奇妙漫画，让阅读漫画成为一种简单而美好的日常。
                  </p>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20and%20inviting%20modern%20office%20space%20with%20people%20casually%20reading%20manga%20on%20tablets%20and%20phones%2C%20cozy%20sofas%20and%20bean%20bags%2C%20walls%20decorated%20with%20colorful%20comic%20art%20prints%2C%20natural%20light%20from%20large%20windows%2C%20creative%20startup%20atmosphere%2C%20warm%20wooden%20furniture%2C%20relaxed%20and%20creative%20vibe&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                  alt="奇妙漫画团队 - 为漫画爱好者而生"
                  className="w-full rounded-2xl object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                我们的成绩
              </h2>
              <p className="text-foreground-500 text-sm">一路走来，感恩有你</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: `${(siteStats.totalComics / 10000).toFixed(1)}万+`, label: '漫画资源', icon: 'ri-book-2-line' },
                { value: `${(siteStats.totalChapters / 10000).toFixed(1)}万+`, label: '漫画章节', icon: 'ri-file-list-2-line' },
                { value: `${(siteStats.dailyVisitors / 10000).toFixed(1)}万+`, label: '日活跃用户', icon: 'ri-user-smile-line' },
                { value: `${(siteStats.registeredUsers / 10000).toFixed(1)}万+`, label: '注册用户', icon: 'ri-group-line' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100 border border-background-200">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Core Values */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                核心价值
              </h2>
              <p className="text-foreground-500 text-sm">我们坚持的理念与追求</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-heart-pulse-line',
                  title: '用户至上',
                  desc: '一切以读者的阅读体验为核心，从UI设计到功能开发，每一个细节都为读者精心打磨。',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: '内容为王',
                  desc: '严格筛选优质漫画内容，与国内外优秀创作者合作，持续扩充高质量漫画资源库。',
                },
                {
                  icon: 'ri-rocket-line',
                  title: '技术创新',
                  desc: '不断探索优化阅读技术，下拉式阅读、智能预加载、多端同步等创新功能持续升级。',
                },
                {
                  icon: 'ri-open-arm-line',
                  title: '免费开放',
                  desc: '坚持免费阅读模式，降低漫画阅读门槛，让更多爱好者能够无负担地享受漫画文化。',
                },
                {
                  icon: 'ri-global-line',
                  title: '多元包容',
                  desc: '汇聚韩漫、日漫、国漫等多种文化背景的漫画作品，满足不同读者的多元口味。',
                },
                {
                  icon: 'ri-team-line',
                  title: '社区共建',
                  desc: '建设活跃的漫画爱好者社区，鼓励交流讨论和创作分享，共同推动漫画文化发展。',
                },
              ].map((value) => (
                <div key={value.title} className="flex gap-4 p-5 rounded-xl bg-background-50 border border-background-200">
                  <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className={`${value.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1.5">{value.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{value.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                用户口碑
              </h2>
              <p className="text-foreground-500 text-sm">真实用户的评价与反馈</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="p-5 rounded-xl bg-background-100 border border-background-200">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-[10px] text-amber-500"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-500 leading-relaxed">{t.content}</p>
                  <p className="text-[10px] text-foreground-300 mt-3">{t.date}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-500 text-sm">关于奇妙漫画，你可能想知道</p>
            </div>
            <div className="space-y-3">
              {faqData.map((faq, index) => (
                <details key={index} className="group rounded-xl bg-background-50 border border-background-200 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 group-open:rotate-45 transition-transform duration-200">
                      <i className="ri-add-line"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-foreground-500 text-sm">有任何问题或建议，欢迎随时联系</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                {
                  icon: 'ri-mail-line',
                  title: '电子邮箱',
                  value: 'contact@qimiaomanhua.com',
                  href: 'mailto:contact@qimiaomanhua.com',
                },
                {
                  icon: 'ri-wechat-line',
                  title: '微信公众号',
                  value: '奇妙漫画',
                  href: '#',
                },
                {
                  icon: 'ri-weibo-line',
                  title: '新浪微博',
                  value: '@奇妙漫画官方',
                  href: '#',
                },
                {
                  icon: 'ri-red-packet-line',
                  title: '小红书',
                  value: '奇妙漫画',
                  href: '#',
                },
                {
                  icon: 'ri-customer-service-2-line',
                  title: '客服微信',
                  value: 'qimiaomh',
                  href: '#',
                },
                {
                  icon: 'ri-map-pin-line',
                  title: '公司地址',
                  value: '北京市朝阳区奇妙漫画大厦',
                  href: '#',
                },
              ].map((contact) => (
                <a
                  key={contact.title}
                  href={contact.href}
                  className="flex flex-col items-center gap-3 p-6 rounded-xl bg-background-100 border border-background-200 hover:border-primary-300 hover:-translate-y-0.5 transition-all duration-200"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${contact.icon} text-xl`}></i>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-foreground-400 mb-1">{contact.title}</p>
                    <p className="text-sm font-medium text-foreground-800">{contact.value}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}